// The outside's contract: what the screens hand to whatever draws the frame
// around them (the menu, each screen's title and buttons) and what comes back
// (an entry or a button pressed). Plain data, the same for the web's own frame
// and for a native app (SwiftUI, Jetpack Compose) that shows the screens in a
// web view and draws the frame with its own parts.
//
// Promise:
// - Data only: no markup, no colors, nothing of one OS. Icons are names
//   (`ShellIconName`); each app maps them to its own (SF Symbols, Material
//   Symbols) and shows a plain one for a name it does not know.
// - Every message and every menu or header carries `version`
//   (`SHELL_CONTRACT_VERSION`). Versions only add: fields and kinds of entries
//   are added, never taken away or given another meaning.
// - A reader ignores what it does not know: unknown fields, unknown kinds of
//   entries or messages, and entries missing what they need are skipped, not
//   failed on (`readShellMenu`, `readShellHeader`, `readFromHost`).
// - Pressing comes back by id only (`select`, `action`): the screen decides
//   what an id does, so the outside never needs to know.

/**
 * Raised when the contract gains something. Readers accept any version.
 * 2: menu items' `danger` and `disabled` (the account's and place's menus).
 * 3: the rail of places (`ShellMenu.rail`); a header's chips beside its title
 *    (`chips`), its note (`note`) and a button's `style`.
 * 4: the Dock's state (`dock`): out of sight while something over the
 *    screen's bottom asks it to or a field is being written in.
 * 5: floating parts (`floats`): what floats over a screen (a conversation's
 *    way to its latest message), drawn by the host instead of the web when
 *    the host says it draws that kind (`ShellHostInfo`).
 * 6: a page's own buttons on the phone's Dock (`tools`): a document's
 *    undo, redo and outline.
 * 7: a conversation's post box (`composer`), drawn by a host that says it
 *    draws the "composer" kind: its text, attaching files and sending go
 *    through messages (`composer` both ways).
 * 8: the post box's conditions (`ShellComposer.pills`: an Agent chat's
 *    device and model), pressed back as the `pill` event.
 * 9: a conversation's "your turn" line (`floats`, the "turn" kind): what the
 *    agent asks, folded to one line right above the post box; pressing it
 *    opens the web's drawer of it. Its `tag`, `note` and `fresh`.
 * 10: candidates while writing in the post box (`ShellComposer.suggest`:
 *    people after "@", links after "[[", emoji after ":"), from the text and
 *    the caret the host hands (`text` with `caret`); one chosen comes back as
 *    `pick`, and the web puts the caret after it (`ShellComposer.caret`).
 * 11: an attached file's picture once it is up, its number in the text and
 *    its size (`ShellComposerFile.url`, `number`, `size`).
 * 12: the Dock's places as the person keeps them on this device
 *    (`ShellMenu.dock`: their order and the ones left out).
 * 13: a host that frames its screens as a phone's at any width
 *    (`ShellHostInfo.phone`: the iPhone Duo opened keeps the phone's frame).
 * 14: the keyboard over the screens' window as the host measures it
 *    (`keyboard`): the phone's frame keeps to it, and only while a field of
 *    this window is written in.
 * 15: a button drawn as a ring filled to a share (`ShellAction.meter`: a
 *    chat's context window).
 * 16: a workspace's icon on its tile (`ShellPlaceMark` "tile" `image`), and
 *    the account's and the tile's images as whole URLs read with the screen's
 *    sign-in.
 * 17: the host's own notifications (`ShellHostInfo.notices`): it says how
 *    they stand (`notices` back, asked or on its own), the screens ask it to
 *    ask the person (`notices` with `ask`), set its badge (`badge`) and take
 *    away those of a chat read (`notices-clear`); a notification pressed
 *    opens its screen (`open`).
 * 18: the host's own settings (`ShellHostInfo.appSettings`): the settings
 *    show one row under "This app" that asks it to open them
 *    (`app-settings`), and leave out this app's items (the look, the keys),
 *    which the host keeps for all its screens.
 * 19: "your turn" inside the post box a host draws (`ShellComposer.turn`,
 *    for a host that says it draws the "composer-turn" kind): the item as
 *    data, folded to its strip or opened; what the person does with it comes
 *    back as the `turn` event of `composer`. The page of an item's UI alone
 *    (`ShellComposerTurnUi.url`), which tells its height (`turn-ui-height`).
 * 20: "your turn"'s body drawn as Markdown by the host: its pictures' file
 *    ids (`ShellComposerTurnImage.id`), and a file or a reference in it
 *    pressed (`file`, `ref`), which the web opens as its own body would.
 * 21: a place's own sidebar beside its pages (`ShellPlace.sidebar`, the
 *    desktop app draws it): which entries go over its line (its own things
 *    and Me's), the Workspace Me's pages are narrowed to there, its favorites
 *    (`ShellFavorites`, changed back with `favorite`) and what goes on in it
 *    (`ShellOngoing`, the call one is in).
 * 22: something lies over the whole window (`cover`: a dialog's dimmed
 *    ground): the host dims what it draws around the screens the same, and a
 *    press on it comes back (`cover-pressed`), as a press beside the dialog.
 * 23: the host shows pictures and videos large itself
 *    (`ShellHostInfo.viewer`): the web hands what was pressed (`viewer`: the
 *    images to step through, where each sits on the screen) instead of
 *    opening its own viewer; the host tells where it stepped
 *    (`viewer-moved`, answered with `viewer-rect`) and that it closed
 *    (`viewer-closed`, with a video's position).
 * 24: a header of something over the whole screen (`ShellHeader.over`: a
 *    dialog that fills a phone's screen hands its bar to the host): no way
 *    back to the page under it; its own way back, when it has one, is an
 *    action (`ShellHeader.backAction`).
 * 25: the rail's way to make a new place can be the page shown now
 *    (`ShellRailNew.current`: its making page is open).
 * 26: the host's edge swipe over something over the whole screen with a way
 *    back of its own (`ShellHeader.backAction`) moves it with the finger
 *    (`over-drag`, how far right) and lets go (`over-drag-end`: back, or not:
 *    it goes back where it was), as a screen pushed from the right.
 * 27: a draft's text written in a page of its own (`ShellComposerTurnDraft.
 *    page`): the web's frame, which looks like what is sent, not like its
 *    Markdown. It tells its height (`turn-ui-height`), what is written
 *    (`turn-draft-text`; the host hands that back as `draft`, as before) and
 *    whether it is written in (`turn-draft-focus`).
 * 28: an item's videos come with its pictures (`ShellComposerTurnImage.
 *    video`): the host draws a video's first frame where the body has it,
 *    and pressing it asks the web to open it (`file`).
 * 29: a button's menu has titled parts (`ShellAction.group`) and menus
 *    inside it (an item with `items`); a button can open a panel the host
 *    draws from data (`ShellAction.panel`: rows, meters, a button of its
 *    own), as it draws a menu. Pressing the panel's button sends its id.
 *    An item can show a color's dot (`color`), be checked (`on`) and ask
 *    for words before it is sent (`prompt`: the words come with the press,
 *    `action.text`); a chip can open a menu (`ShellChip.items`).
 */
export const SHELL_CONTRACT_VERSION = 29;

/**
 * An icon by name. The names in use are ui-core's icon kinds ("inbox",
 * "cases", "agent", "people", "documents", "timeblocker", "star", "share",
 * "more", ...). Unknown names get the app's plain icon.
 */
export type ShellIconName = string;

/** What an entry shows at its end: a dot, or a count. */
export type ShellBadge =
  | {
      kind: "dot";
      /** "accent": something to look at; "danger": something running. */
      tone: "accent" | "danger";
      /** Read aloud (the dot has no text of its own). */
      label: string;
    }
  | { kind: "count"; count: number; label: string };

/** A place to go: pressing it sends `select` with its id. */
export interface ShellMenuItem {
  type: "item";
  /** Stable across sessions: `me:<path>`, `place:<id>`, `list:<id>`. */
  id: string;
  label: string;
  icon?: ShellIconName;
  /** The path it opens (for marking the current entry; going is the screen's). */
  to?: string;
  /** More paths where it is the current entry. */
  paths?: string[];
  badge?: ShellBadge;
  /** Drawn as one that cannot be undone (sign out, delete). Since 2. */
  danger?: boolean;
  /** Shown but cannot be chosen. Since 2. */
  disabled?: boolean;
}

export interface ShellMenuSeparator {
  type: "separator";
}

/** A heading over the entries after it (a workspace's spaces). */
export interface ShellMenuHeading {
  type: "heading";
  label: string;
}

export type ShellMenuEntry =
  | ShellMenuItem
  | ShellMenuSeparator
  | ShellMenuHeading;

/** Who the menu is of (the account at its top). */
export interface ShellAccount {
  name: string;
  /** The picture's whole URL, when there is one (read with the screen's sign-in). */
  image?: string;
  /** The account's menu (settings, shortcuts, sign out). */
  menu: ShellMenuEntry[];
}

/** The other place beside Me (cloud: a workspace) and its own menu. */
export interface ShellPlace {
  id: string;
  name: string;
  /** Switch to another place, its settings, a new one. */
  menu: ShellMenuEntry[];
  /** Its own sidebar beside its pages. Since 21. */
  sidebar?: ShellPlaceSidebar;
}

/**
 * A place's own sidebar (a workspace's), as the web draws it beside the
 * place's pages: the place's entries of the menu (`place:<id>`) with Me's it
 * names, a line, the rest, its favorites, then what goes on. Since 21.
 */
export interface ShellPlaceSidebar {
  /**
   * Over the line, in order: ids of the place's entries and of Me's
   * (`me:<path>`) drawn there. The place's other entries follow the line in
   * the menu's order.
   */
  mine: string[];
  /**
   * The Workspace (its id) Me's pages are narrowed to when opened from here.
   * `label` was replaced by `workspace` without a new version: an older app
   * and this one do not work together.
   */
  workspace?: string;
  /** The menu of its head (its settings, inviting, a new place). */
  menu?: ShellMenuEntry[];
  favorites?: ShellFavorites;
  ongoing?: ShellOngoing[];
}

/** A favorite: pressing it goes to `to`. Since 21. */
export interface ShellFavoriteItem {
  type: "item";
  id: string;
  label: string;
  icon?: ShellIconName;
  to: string;
}

/** A folder of favorites (one level). Since 21. */
export interface ShellFavoriteFolder {
  type: "folder";
  id: string;
  label: string;
  items: ShellFavoriteItem[];
}

export type ShellFavoriteEntry = ShellFavoriteItem | ShellFavoriteFolder;

/**
 * The favorites kept for a place, in the person's order. Changes come back as
 * `favorite` (moved, a folder made, renamed or deleted); the web makes them
 * and sends the menu again. Since 21.
 */
export interface ShellFavorites {
  /** Which place's they are (sent back with each change). */
  place: string;
  entries: ShellFavoriteEntry[];
}

/** Something going on in the place one can go back to (the call one is in). Since 21. */
export interface ShellOngoing {
  id: string;
  label: string;
  icon?: ShellIconName;
  to: string;
  /** A short line under it (who is in, a screen shared). */
  detail?: string;
  /** When it began (ISO 8601), for its running time. */
  since?: string;
}

/** How a place is pictured on the rail. */
export type ShellPlaceMark =
  /** trameria's own mark (Me). */
  | { kind: "brand" }
  /**
   * A workspace's tile: its icon (a whole URL, read with the screen's
   * sign-in) when it has one, else its first letter on one of six grounds (1-6).
   */
  | { kind: "tile"; initial: string; ground: number; image?: string };

/** One place on the rail: pressing it sends `select` with its id. */
export interface ShellRailPlace {
  type: "place";
  /** `rail:<place>` (`rail:me`, `rail:<workspace slug>`). */
  id: string;
  name: string;
  mark: ShellPlaceMark;
  /** Where pressing goes: the page it was left on, or its home when shown now. */
  to: string;
  /** The place shown now. */
  current?: boolean;
}

/** The rail's way to make a new place. */
export interface ShellRailNew {
  type: "new";
  id: string;
  label: string;
  to: string;
  /** Its making page is the page shown now (no place is). Since 25. */
  current?: boolean;
}

export type ShellRailEntry = ShellRailPlace | ShellRailNew;

/** The places one moves between (cloud: Me, then the workspaces, then "new"). Since 3. */
export interface ShellRail {
  /** Read aloud for the rail. */
  label: string;
  entries: ShellRailEntry[];
}

export interface ShellMenu {
  version: number;
  account?: ShellAccount;
  place?: ShellPlace;
  /** Me's entries, a separator, the place's entries, then its lists under headings. */
  entries: ShellMenuEntry[];
  /** Since 3. Absent where there is one place only (the offline edition). */
  rail?: ShellRail;
  /**
   * The Dock's places as the person keeps them on this device, by entry id:
   * their order (ids it does not name come after, in the menu's order) and
   * the ones left out (in the menu only). Absent: never set (the host's own
   * arrangement). Since 12.
   */
  dock?: ShellDockPrefs;
}

/** See `ShellMenu.dock`. Since 12. */
export interface ShellDockPrefs {
  order: string[];
  hidden: string[];
}

/** A button at a screen's top right (the star, share, "..."). */
export interface ShellAction {
  id: string;
  label: string;
  icon?: ShellIconName;
  /** A toggle shows `on` (the star). */
  on?: boolean;
  /**
   * The button is a ring filled to this share, 0 to 1 (a chat's context
   * window, icon "contextPie"); absent while unknown (the ring dashed).
   * Since 15.
   */
  meter?: number;
  disabled?: boolean;
  danger?: boolean;
  /**
   * Present: the button opens these (the "..." menu); pressing one sends its
   * id. An item with items of its own opens them as a menu inside (since 29).
   */
  items?: (ShellAction | ShellMenuSeparator)[];
  /**
   * In a menu, with `items`: a titled part of the menu it is in (its label
   * the title), not an item of its own. Since 29.
   */
  group?: boolean;
  /**
   * Present: pressing it opens this panel, which the host draws (instead of
   * sending its id): what the web shows hanging from the button. Since 29.
   */
  panel?: ShellPanel;
  /** In a menu: a dot of this color (`#rrggbb`) in place of its icon. Since 29. */
  color?: string;
  /**
   * In a menu: pressing it asks for words first (a new label's name); they
   * come with the press (`action.text`). Since 29.
   */
  prompt?: { title: string; placeholder?: string; max?: number };
  /**
   * How it is shown: "icon" (an icon button, the default) or "pill" (its
   * label as a small button: "Done"). Since 3.
   */
  style?: "icon" | "pill";
}

/**
 * A panel a button opens, drawn by the host (`ShellAction.panel`): a title
 * with a button of its own at its end, what went wrong last, and its blocks
 * top to bottom. Since 29.
 */
export interface ShellPanel {
  title: string;
  /** At the title's end (a chat's "compact"): pressing sends its id. */
  button?: { id: string; label: string; disabled?: boolean };
  /** What went wrong with the button, in the danger color. */
  failure?: string;
  blocks: ShellPanelBlock[];
}

export type ShellPanelBlock =
  /** Names and their values, the values at the end (tokens, cost). */
  | { type: "rows"; rows: { label: string; value: string }[] }
  /** A quiet line (nothing yet, why something is missing). */
  | { type: "text"; text: string }
  /** A part's title over a line, with a quiet word at its end. */
  | { type: "heading"; label: string; detail?: string }
  /**
   * A bar filled to `share` (0 to 1), its value at the end; `mark`: a line
   * across it (how far a window has run); `danger`: ahead of it.
   */
  | {
      type: "meter";
      label: string;
      detail?: string;
      value: string;
      share: number;
      mark?: number;
      danger?: boolean;
    };

/** A chip beside a screen's title (a Case's label): pressing sends `action`. Since 3. */
export interface ShellChip {
  id: string;
  label: string;
  icon?: ShellIconName;
  /** Present: pressing it opens these as a menu, as a button's. Since 29. */
  items?: (ShellAction | ShellMenuSeparator)[];
}

/** A screen's title and buttons. */
export interface ShellHeader {
  version: number;
  title: string;
  /** The screen has a way back (to its list). */
  back?: boolean;
  /**
   * Of something lying over the whole screen (a dialog filling a phone's
   * screen), not of the page under it: the way back to the page before is
   * not shown (`back` is ignored). Since 24.
   */
  over?: boolean;
  /**
   * Its way back is this action (pressed back as `action` with this id): a
   * step back inside what lies over the screen, not a page back. Since 24.
   */
  backAction?: string;
  /** Beside the title, in order. Since 3. */
  chips?: ShellChip[];
  /** A quiet line before the buttons ("Done · Oct 28"). Since 3. */
  note?: string;
  /** In order, as the web draws them left to right; the "..." last. */
  actions: ShellAction[];
}

/**
 * A kind of part that floats over a screen, which a host may draw with its
 * own parts. "to-end": a conversation's way to its latest message, shown
 * while the person reads further up. "turn" (since 9): what the agent asks,
 * folded to one line right above the post box. "composer-turn" (since 19):
 * not a part of its own but a way of drawing the post box: the host draws
 * what the agent asks inside it (`ShellComposer.turn`), and then never gets
 * the "turn" line.
 */
export type ShellFloatKind = "to-end" | "composer" | "turn" | "composer-turn";

/**
 * What a host says of itself before the screens' first script runs, as
 * `window.trameriaShellHost`. Since 5.
 */
export interface ShellHostInfo {
  version: number;
  /**
   * The floating parts the host draws: the web hands these as data
   * (`floats`) and does not draw them. Kinds the web does not know are kept.
   */
  floats: string[];
  /**
   * The host frames its screens as a phone's whatever their width (a phone
   * that opens wider than a phone, the iPhone Duo): they keep the phone's
   * frame, as below a phone's width. Absent: by the width. Since 13.
   */
  phone?: boolean;
  /**
   * The host shows notifications of its own (the iOS app's, through APNs):
   * this installation of it and the device it runs on. Since 17.
   */
  notices?: ShellHostNotices;
  /**
   * The host has settings of its own (the iOS app's), opened with
   * `app-settings`. Since 18.
   */
  appSettings?: boolean;
  /**
   * The host shows pictures and videos large itself: the web sends `viewer`
   * instead of opening its own viewer (except for files not sent yet, which
   * the host cannot read). Since 23.
   */
  viewer?: boolean;
}

export interface ShellHostNotices {
  /** A uuid the app made once for its installation. */
  installationId: string;
  /** `iPhone` or `iPad`. */
  device: string;
}

/** How the host's notifications stand (`notices` from the host). Since 17. */
export interface ShellNoticesState {
  /** The OS's answer: allowed, refused, or not asked yet. */
  status: "authorized" | "denied" | "notDetermined";
  /** The device token (hex), once allowed and registered with the OS. */
  token: string | null;
  /** Which of Apple's services takes the token. */
  environment: "sandbox" | "production";
}

/**
 * Where a floating part rests in the screens' view, in CSS px: its bottom
 * center, `x` from the view's left edge and `bottom` up from its bottom edge.
 * It clears what the web draws under it (a post box).
 */
export interface ShellFloatAnchor {
  x: number;
  bottom: number;
}

/** A floating part shown now. Pressing it sends `action` with its id. Since 5. */
export interface ShellFloat {
  kind: ShellFloatKind;
  id: string;
  /** Read aloud (the part may show only an icon). */
  label: string;
  /** Absent: where the host puts it (over its own Dock). */
  anchor?: ShellFloatAnchor;
  /** Before the label, in the chosen color (a request's kind). Since 9. */
  tag?: string;
  /** After the label, quiet (how many more wait). Since 9. */
  note?: string;
  /** Not seen yet: marked so it is not missed. Since 9. */
  fresh?: boolean;
}

/** A file attached to the post box, as the host shows it. Since 7. */
export interface ShellComposerFile {
  id: string;
  name: string;
  /** An image (the host may show it as a picture). */
  image: boolean;
  /** 0 to 1 while it goes up; 1 once it is up. */
  progress: number;
  /** An image's picture on the server, once it is up. Since 11. */
  url?: string;
  /** An image's number where the text names it ("image 2"). Since 11. */
  number?: number;
  /** Another file's size in words ("12 KB"). Since 11. */
  size?: string;
}

/**
 * A conversation's post box, for a host that draws it (the "composer" kind).
 * The web keeps the draft, the files going up and what sending does; the
 * host draws the box and hands back what the person does in it. Since 7.
 */
export interface ShellComposer {
  /** This box. Another id: another box (another conversation). */
  id: string;
  placeholder: string;
  /**
   * The text as the web holds it. The host puts it in the box when
   * `revision` is new (the box opened, a draft read back, sent, an image's
   * label put in); otherwise the host's own text stands.
   */
  text: string;
  revision: number;
  /** Sending can be done now (something to send, nothing still going up). */
  canSend: boolean;
  /** Read aloud for the send button. */
  sendLabel: string;
  /** The box takes files: attach's label. Absent: no attach. */
  attach?: string;
  files: ShellComposerFile[];
  /** The conditions, in order. Since 8. */
  pills?: ShellComposerPill[];
  /**
   * Where the caret goes in `text` (UTF-16 offset), when the web set the text
   * with the caret somewhere (after a chosen candidate). Since 10.
   */
  caret?: number;
  /** The candidates open now (absent: none). Since 10. */
  suggest?: ShellSuggest;
  /**
   * "Your turn" of the conversation, drawn inside the box (absent: nothing
   * asked, or a host that does not say "composer-turn"). Opened, the box's
   * `text`, `placeholder`, `files` and `attach` are the item's free answer
   * and `id` is the item's own (`turn:<item id>`), so the host's text is
   * swapped as for another box; folded, they are the message's again.
   * `canSend` and `send` stay what Return and the send key do: for kinds
   * whose send key answers, `send` answers; for the others `canSend` is
   * false (Return breaks the line, the answer is one of `buttons`). Since 19.
   */
  turn?: ShellComposerTurn;
}

/**
 * An item of "your turn" as the host draws it in the post box. Data only;
 * every word is the web's (in the person's language). Since 19.
 */
export interface ShellComposerTurn {
  /** The item shown (its id). */
  item: string;
  /** How many items are open; `at` is the one shown, from 0. */
  count: number;
  at: number;
  /** Opened in the box (else its one-line strip over the box). */
  open: boolean;
  /** Not seen yet: the strip is marked so it is not missed. */
  fresh: boolean;
  /** The kind's tag (a request's, a draft's, or the agent asking to change it); absent for a question. */
  tag?: string;
  /** Quiet notes in the tag row, in order (held by, from, the Case, a Slack channel). */
  notes: string[];
  title: string;
  /** Markdown. */
  body?: string;
  /**
   * The pictures the body puts in (its `![name](trameria://file/<id>)`), as
   * whole URLs read with the screen's sign-in: a host that does not draw
   * pictures inside the Markdown draws these under it. Only these: the
   * item's other files are for its UI, not shown.
   */
  images?: ShellComposerTurnImage[];
  /**
   * The places to look at, as chips; pressing one sends `link`. Drawn in the
   * content under the body, wrapping (as the web's box draws them); the row
   * under the content keeps ‹ n/m › and fold alone.
   */
  links?: ShellComposerTurnLink[];
  /** choice: its options (pressing one sends `option`; it only selects). */
  options?: ShellComposerTurnOption[];
  /** choice: several can be selected. */
  multi?: boolean;
  /** form: its fields (written ones send `field`). */
  fields?: ShellComposerTurnField[];
  /** draft: the words to send to someone else. */
  draft?: ShellComposerTurnDraft;
  /** ui: the agent's UI, a page to show in a web view of its own. */
  ui?: ShellComposerTurnUi;
  /**
   * The agent asks to change the item: what it would become and why. The
   * buttons are then consent and keep, nothing else answers.
   */
  proposal?: ShellComposerTurnProposal;
  /** The answer buttons in place of send, left to right; pressing sends `button`. */
  buttons: ShellComposerTurnButton[];
  /** A line under the buttons (what is missing, a refusal, a draft's state). */
  problem?: string;
}

export interface ShellComposerTurnImage {
  url: string;
  name: string;
  /**
   * The file's id, as the body names it (`trameria://file/<id>`): a host
   * drawing the Markdown puts the picture where the body has it. Since 20.
   */
  id?: string;
  /**
   * A video, not a picture: the host draws its first frame (`url` is the
   * video's) with a mark to play, and a press asks the web (`file`). Since 28.
   */
  video?: boolean;
}

export interface ShellComposerTurnLink {
  id: string;
  label: string;
  /** A web page's address: the host may open it itself. */
  url?: string;
}

export interface ShellComposerTurnOption {
  id: string;
  label: string;
  description?: string;
  recommended?: boolean;
  selected: boolean;
}

export interface ShellComposerTurnField {
  id: string;
  label: string;
  type: "text" | "number" | "date";
  /** What is written (a date as YYYY-MM-DD). */
  value: string;
  placeholder?: string;
}

export interface ShellComposerTurnDraft {
  /** The side shown (Markdown); edited, it comes back as `draft`. */
  text: string;
  /** An email's subject (absent: not an email). */
  subject?: string;
  /** It may be edited here (else read only). */
  editable: boolean;
  /**
   * The page that writes the side shown alone, on a clear ground, while it
   * may be edited: a path on the screens' own origin
   * (`/me/agents/turn-draft/<item>?chat=<chat id>&side=<side>`), joined to
   * it as `ShellComposerTurnUi.url` is. It tells its height with
   * `turn-ui-height` and what is written with `turn-draft-text`; it saves
   * nothing itself. Since 27.
   */
  page?: string;
  /** Read aloud for copying the whole text. */
  copyLabel: string;
  /** A translated draft's two sides; pressing one sends `button` with its id. */
  sides?: ShellComposerTurnSide[];
  /** The message it replies to. */
  replyTo?: { text: string; from?: string; url?: string };
  /** A warning under the frame (the side an edit left behind). */
  note?: string;
  /** Buttons under the frame (placing it in Slack); pressing sends `button`. */
  actions?: ShellComposerTurnButton[];
}

export interface ShellComposerTurnSide {
  id: string;
  label: string;
  /** The side shown now. */
  on: boolean;
  /** Said beside it: it still holds what was there before an edit. */
  stale?: string;
}

export interface ShellComposerTurnUi {
  /**
   * The page that draws the UI alone, on a clear ground, and answers the
   * item itself (with the same sign-in and storage). A path on the screens'
   * own origin (`/me/agents/turn-ui/<item>?chat=<chat id>`): the host joins
   * it to the origin it loads the screens from. It tells its height with
   * `turn-ui-height`.
   */
  url: string;
  /** A first height (px) before the page tells one. */
  height?: number;
}

export interface ShellComposerTurnProposal {
  /** The new content's title and body (Markdown). */
  title: string;
  body?: string;
  reason?: string;
}

export interface ShellComposerTurnButton {
  id: string;
  label: string;
  /** The main answer (yes, OK, done, answer): drawn in the chosen color. */
  primary?: boolean;
  enabled: boolean;
}

/** What the person did with the item in the box (the `turn` event). Since 19. */
export type ShellComposerTurnEvent =
  /** The strip pressed: open the item. */
  | { action: "open" }
  /** Folded back to its strip. */
  | { action: "fold" }
  /** The item before (-1) or after (1). */
  | { action: "step"; by: -1 | 1 }
  /** An option pressed (it selects or unselects it). */
  | { action: "option"; option: string }
  /** A form's field written in. */
  | { action: "field"; field: string; value: string }
  /** A draft's text (and an email's subject) edited, once the person is done. */
  | { action: "draft"; text: string; subject?: string }
  /** A button pressed (an answer, a draft's side or action). */
  | { action: "button"; button: string }
  /** A place to look at pressed (its id). */
  | { action: "link"; link: string }
  /**
   * A file of the body or a proposal's body pressed (`trameria://file/<id>`):
   * the web shows a picture or a video large, or downloads another file. Since 20.
   */
  | { action: "file"; file: string }
  /**
   * A reference of the body pressed (`[[kind:id|name]]`, its id as `ref`:
   * the event's `id` is the box's): the web opens it. Since 20.
   */
  | { action: "ref"; kind: string; ref: string };

/**
 * A condition the post box runs with (an Agent chat's device or model), shown
 * as a pill in the box. Pressing it sends the `pill` event; the web opens
 * what picks it. Since 8.
 */
export interface ShellComposerPill {
  id: string;
  label: string;
  /** Read aloud (the label alone does not say what it is). */
  readAloud: string;
  /** A dot before the label: the device's state. */
  dot?: "ready" | "off" | "failed";
  /** Something needs a look (a mark after the label). */
  warn?: boolean;
}

/**
 * What a candidate shows before its name: a person's picture or first letter
 * on one of five pale grounds (1-5), an emoji, a picture (a custom emoji), or
 * an icon by name. Since 10.
 */
export type ShellSuggestLook =
  | { kind: "avatar"; initial: string; ground: number; image?: string }
  | { kind: "emoji"; text: string }
  | { kind: "image"; url: string }
  | { kind: "icon"; name: ShellIconName };

/** One candidate in the list over the post box. Since 10. */
export interface ShellSuggestItem {
  key: string;
  label: string;
  /** A short note at the row's end. */
  note?: string;
  /** Still chosen, but shown fainter (the note says why). */
  muted?: boolean;
  look?: ShellSuggestLook;
}

/** The list of candidates open over the post box. Since 10. */
export interface ShellSuggest {
  /** Read aloud for the list ("People"). */
  label: string;
  items: ShellSuggestItem[];
  /** Said while still looking (no items yet). */
  loading?: string;
  /** Said when nothing is found. */
  empty: string;
}

/** A file the host hands to the post box: its bytes as base64. Since 7. */
export interface ShellComposerUpload {
  name: string;
  mime: string;
  data: string;
}

/** From the screens to the outside. */
export type ShellMessageToHost =
  | { version: number; type: "menu"; menu: ShellMenu }
  /** null: the screen has no header (it goes, or draws none). */
  | { version: number; type: "header"; header: ShellHeader | null }
  | { version: number; type: "location"; path: string }
  /**
   * At a phone's width, where the host draws the Dock: whether it is to be
   * out of sight now (a drawer over the screen's bottom, a field being
   * written in), as the web's own Dock sinks away then. `covered`: something
   * lies over the screen (a drawer): what the host draws over the top (its
   * way back, the screen's buttons) goes too, so the drawer's own controls
   * there can be pressed. Since 4.
   */
  | { version: number; type: "dock"; hidden: boolean; covered?: boolean }
  /**
   * Every floating part shown now, of the kinds the host draws (one that
   * goes from the list is out of sight). Since 5.
   */
  | { version: number; type: "floats"; floats: ShellFloat[] }
  /**
   * At a phone's width, where the host draws the Dock: the page's own buttons
   * on it, in order (a document's undo, redo and outline; none: an empty
   * list). A list's "+" is not among them: its header's `add` is the same. Pressing one, or an item of its menu, sends `action`.
   * Since 6.
   */
  | { version: number; type: "tools"; tools: ShellAction[] }
  /** The post box shown now, for a host that draws it (null: none). Since 7. */
  | { version: number; type: "composer"; composer: ShellComposer | null }
  /**
   * Asks how the host's notifications stand (it answers `notices`); `ask`:
   * asks the person first when they were never asked, or opens the host's
   * notification settings when they refused. Since 17.
   */
  | { version: number; type: "notices"; ask?: boolean }
  /** The app's badge (0 clears it). Since 17. */
  | { version: number; type: "badge"; count: number }
  /** Takes away the notifications of a chat read (their tag). Since 17. */
  | { version: number; type: "notices-clear"; tag: string }
  /** Opens the host's own settings (`ShellHostInfo.appSettings`). Since 18. */
  | { version: number; type: "app-settings" }
  /**
   * From the page of an item's UI alone (`ShellComposerTurnUi.url`), in the
   * web view the host made for it: how tall its content is now (CSS px), on
   * load and whenever it changes, so the host sizes that view. 0 once the
   * item is no longer open there. Since 19.
   */
  | { version: number; type: "turn-ui-height"; item: string; height: number }
  /**
   * From the page of a draft's text alone (`ShellComposerTurnDraft.page`):
   * what is written now, as Markdown, at each change, and once more with
   * `done` when the field is left changed. The host hands it to the web as
   * the `draft` event (at `done`, and before any press in the box). Since 27.
   */
  | {
      version: number;
      type: "turn-draft-text";
      item: string;
      text: string;
      done: boolean;
    }
  /**
   * From the page of a draft's text alone: its field took or left the
   * keyboard, so the host keeps it within the room above the keyboard.
   * Since 27.
   */
  | {
      version: number;
      type: "turn-draft-focus";
      item: string;
      focused: boolean;
    }
  /**
   * What lies over the whole window now (null: nothing): the host lays the
   * same over what it draws around the screens. Since 22.
   */
  | { version: number; type: "cover"; cover: ShellCover | null }
  /**
   * A picture or a video pressed, for a host that shows them itself
   * (`ShellHostInfo.viewer`). `id` is new for each opening; the host's
   * `viewer-moved` and `viewer-closed` carry it. Since 23.
   */
  | ({ version: number; type: "viewer" } & ShellViewer)
  /**
   * Where the image the host stepped to (`viewer-moved`) sits on the screen
   * now, for closing into it (absent: out of sight). Since 23.
   */
  | {
      version: number;
      type: "viewer-rect";
      id: string;
      index: number;
      rect?: ShellRect;
    };

/** A box in the screens' view, in CSS px from its top left. Since 23. */
export interface ShellRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** What the host shows large (`viewer`). Since 23. */
export interface ShellViewer {
  id: string;
  kind: "image" | "video";
  /** The one pressed, in `items`. */
  index: number;
  /**
   * Images: the ones to step through, in order (the pressed one alone when
   * there is nothing to step to). Videos: the one video.
   */
  items: ShellViewerItem[];
  /** A video's position to start from (seconds). */
  startAt?: number;
}

export interface ShellViewerItem {
  /** The whole URL, read with the screens' sign-in. */
  url: string;
  name: string;
  /** The whole URL to download it from; absent: it may not be downloaded. */
  download?: string;
  /** Who put it there and when (as the screens show the time). */
  author?: string;
  at?: string;
  /** Where it sits on the screen now (its thumbnail); absent: out of sight. */
  rect?: ShellRect;
}

/**
 * A dimmed ground over the whole window (a dialog's, an image viewer's). Since 22.
 */
export interface ShellCover {
  /** How dark: black at this opacity (0 to 1). */
  shade: number;
  /**
   * A press on it closes what lies over (as a press beside a dialog): the
   * host sends `cover-pressed`. False: a press does nothing.
   */
  dismiss: boolean;
}

/** A favorite or folder, by its kind and id. Since 21. */
export interface ShellFavoriteRef {
  type: "item" | "folder";
  id: string;
}

/** What `favorite` changes. Since 21. */
export type ShellFavoriteChange =
  /** Into a folder (null: the top; a folder always goes there), before another (null: last). */
  | {
      op: "move";
      entry: ShellFavoriteRef;
      folder: string | null;
      before: ShellFavoriteRef | null;
    }
  | { op: "folder-new"; name: string }
  | { op: "folder-rename"; id: string; name: string }
  | { op: "folder-delete"; id: string };

/** From the outside to the screens. */
export type ShellMessageFromHost =
  /** A menu entry (or an account / place menu item) pressed. */
  | { type: "select"; id: string }
  /** A change to a place's favorites (`ShellFavorites`). Since 21. */
  | ({ type: "favorite"; place: string } & ShellFavoriteChange)
  /**
   * How far the keyboard reaches up over the screens' window (px, 0 while it
   * is down), as the host lays it out for this window. Since 14. Two windows
   * of the app side by side both lie under the keyboard, but only the one
   * whose field is written in makes room (the other one's screen had moved
   * up: the iPhone Duo, the person, 2026-10-30).
   */
  | { type: "keyboard"; height: number }
  /**
   * A header button (or an item of its menu) pressed; `text`: the words an
   * item with a `prompt` asked for (since 29).
   */
  | { type: "action"; id: string; text?: string }
  /** The outside's back button. */
  | { type: "back" }
  /**
   * The host's edge swipe over a header `over` with a `backAction`: how far
   * right (px) the thing over the screen follows the finger. Since 26.
   */
  | { type: "over-drag"; x: number }
  /** The swipe let go: `back` takes its way back, else it returns. Since 26. */
  | { type: "over-drag-end"; back: boolean }
  /** A press on the host's part of the cover (`ShellCover.dismiss`). Since 22. */
  | { type: "cover-pressed" }
  /** The host's viewer stepped to another image (`ShellViewer.items`). Since 23. */
  | { type: "viewer-moved"; id: string; index: number }
  /**
   * The host's viewer closed, on the image it showed last; a video's position
   * then (seconds). Since 23.
   */
  | { type: "viewer-closed"; id: string; index: number; time?: number }
  /** How the host's notifications stand (asked, or changed). Since 17. */
  | ({ type: "notices" } & ShellNoticesState)
  /** A notification pressed: open its screen (a path). Since 17. */
  | { type: "open"; path: string }
  /**
   * What the person did in the post box the host draws (`id`: the box), since 7:
   * - `text`: the text changed;
   * - `send`: send what is in the box;
   * - `attach`: files picked (their bytes);
   * - `remove`: a file taken off (its id);
   * - `room`: how far up the box reaches from the bottom of the screens'
   *   view (the keyboard under it included), so the conversation's end rests
   *   above it; `keyboard`: the host's keyboard is up (the web then lays
   *   nothing out for a keyboard of its own).
   */
  | {
      type: "composer";
      id: string;
      event: "text";
      text: string;
      /** Where the caret is (UTF-16 offset), for candidates. Since 10. */
      caret?: number;
    }
  /** A candidate chosen (its key). Since 10. */
  | { type: "composer"; id: string; event: "pick"; key: string }
  | { type: "composer"; id: string; event: "send" }
  | {
      type: "composer";
      id: string;
      event: "attach";
      files: ShellComposerUpload[];
    }
  | { type: "composer"; id: string; event: "remove"; file: string }
  /** A pill pressed (its id). Since 8. */
  | { type: "composer"; id: string; event: "pill"; pill: string }
  /** What was done with "your turn" in the box (`ShellComposer.turn`). Since 19. */
  | ({
      type: "composer";
      id: string;
      event: "turn";
      /**
       * The item it was done with (`ShellComposerTurn.item`): the web drops
       * one done with an item no longer shown there.
       */
      item: string;
    } & ShellComposerTurnEvent)
  | {
      type: "composer";
      id: string;
      event: "room";
      height: number;
      keyboard?: boolean;
    };

// --- Readers: tolerant of what they do not know ---------------------------

type Raw = Record<string, unknown>;
const isObj = (v: unknown): v is Raw =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined =>
  typeof v === "string" ? v : undefined;
const strs = (v: unknown): string[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === "string")
    : undefined;
const list = <T>(v: unknown, read: (x: unknown) => T | null): T[] =>
  Array.isArray(v) ? v.map(read).filter((x): x is T => x !== null) : [];
const version = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) ? v : SHELL_CONTRACT_VERSION;

/** Drops undefined fields, so a read value equals the one written. */
function clean<T extends object>(o: T): T {
  for (const k of Object.keys(o) as (keyof T)[])
    if (o[k] === undefined) delete o[k];
  return o;
}

function readBadge(v: unknown): ShellBadge | null {
  if (!isObj(v)) return null;
  const label = str(v.label) ?? "";
  if (v.kind === "dot")
    return {
      kind: "dot",
      tone: v.tone === "danger" ? "danger" : "accent",
      label,
    };
  if (v.kind === "count" && typeof v.count === "number")
    return { kind: "count", count: v.count, label };
  return null;
}

function readItem(v: unknown): ShellMenuItem | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  const label = str(v.label);
  if (!id || label === undefined) return null;
  return clean({
    type: "item" as const,
    id,
    label,
    icon: str(v.icon),
    to: str(v.to),
    paths: strs(v.paths),
    badge: readBadge(v.badge) ?? undefined,
    danger: v.danger === true ? true : undefined,
    disabled: v.disabled === true ? true : undefined,
  });
}

function readEntry(v: unknown): ShellMenuEntry | null {
  if (!isObj(v)) return null;
  if (v.type === "item") return readItem(v);
  if (v.type === "separator") return { type: "separator" };
  if (v.type === "heading") {
    const label = str(v.label);
    return label === undefined ? null : { type: "heading", label };
  }
  return null;
}

function readMark(v: unknown): ShellPlaceMark | null {
  if (!isObj(v)) return null;
  if (v.kind === "brand") return { kind: "brand" };
  if (v.kind === "tile" && typeof v.ground === "number")
    return clean({
      kind: "tile" as const,
      initial: str(v.initial) ?? "?",
      ground: v.ground,
      image: str(v.image),
    });
  return null;
}

function readRailEntry(v: unknown): ShellRailEntry | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  const to = str(v.to);
  if (!id || !to) return null;
  if (v.type === "new") {
    const label = str(v.label);
    if (label === undefined) return null;
    return clean({
      type: "new" as const,
      id,
      label,
      to,
      current: v.current === true ? true : undefined,
    });
  }
  if (v.type !== "place") return null;
  const mark = readMark(v.mark);
  if (!mark) return null;
  return clean({
    type: "place" as const,
    id,
    name: str(v.name) ?? "",
    mark,
    to,
    current: v.current === true ? true : undefined,
  });
}

function readFavoriteItem(v: unknown): ShellFavoriteItem | null {
  if (!isObj(v) || v.type !== "item") return null;
  const id = str(v.id);
  const to = str(v.to);
  if (!id || !to) return null;
  return clean({
    type: "item" as const,
    id,
    label: str(v.label) ?? "",
    icon: str(v.icon),
    to,
  });
}

function readFavoriteEntry(v: unknown): ShellFavoriteEntry | null {
  if (!isObj(v)) return null;
  if (v.type === "item") return readFavoriteItem(v);
  if (v.type !== "folder") return null;
  const id = str(v.id);
  if (!id) return null;
  return {
    type: "folder",
    id,
    label: str(v.label) ?? "",
    items: list(v.items, readFavoriteItem),
  };
}

function readOngoing(v: unknown): ShellOngoing | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  const to = str(v.to);
  if (!id || !to) return null;
  return clean({
    id,
    label: str(v.label) ?? "",
    icon: str(v.icon),
    to,
    detail: str(v.detail),
    since: str(v.since),
  });
}

function readPlaceSidebar(v: unknown): ShellPlaceSidebar | undefined {
  if (!isObj(v)) return undefined;
  const favorites =
    isObj(v.favorites) && str(v.favorites.place)
      ? {
          place: str(v.favorites.place) as string,
          entries: list(v.favorites.entries, readFavoriteEntry),
        }
      : undefined;
  return clean({
    mine: strs(v.mine) ?? [],
    workspace: str(v.workspace),
    menu: Array.isArray(v.menu) ? list(v.menu, readEntry) : undefined,
    favorites,
    ongoing: Array.isArray(v.ongoing)
      ? list(v.ongoing, readOngoing)
      : undefined,
  });
}

export function readShellMenu(v: unknown): ShellMenu | null {
  if (!isObj(v)) return null;
  const account = isObj(v.account)
    ? clean({
        name: str(v.account.name) ?? "",
        image: str(v.account.image),
        menu: list(v.account.menu, readEntry),
      })
    : undefined;
  const placeId = isObj(v.place) ? str(v.place.id) : undefined;
  const place =
    isObj(v.place) && placeId
      ? clean({
          id: placeId,
          name: str(v.place.name) ?? "",
          menu: list(v.place.menu, readEntry),
          sidebar: readPlaceSidebar(v.place.sidebar),
        })
      : undefined;
  const dock = isObj(v.dock)
    ? { order: strs(v.dock.order) ?? [], hidden: strs(v.dock.hidden) ?? [] }
    : undefined;
  const rail = isObj(v.rail)
    ? {
        label: str(v.rail.label) ?? "",
        entries: list(v.rail.entries, readRailEntry),
      }
    : undefined;
  return clean({
    version: version(v.version),
    account,
    place,
    entries: list(v.entries, readEntry),
    rail,
    dock,
  });
}

function readAction(v: unknown): ShellAction | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  const label = str(v.label);
  if (!id || label === undefined) return null;
  return clean({
    id,
    label,
    icon: str(v.icon),
    on: typeof v.on === "boolean" ? v.on : undefined,
    meter:
      typeof v.meter === "number" && v.meter >= 0 && v.meter <= 1
        ? v.meter
        : undefined,
    disabled: v.disabled === true ? true : undefined,
    danger: v.danger === true ? true : undefined,
    items: Array.isArray(v.items) ? list(v.items, readActionItem) : undefined,
    group: v.group === true ? true : undefined,
    panel: readPanel(v.panel),
    color:
      typeof v.color === "string" && /^#[0-9a-f]{6}$/i.test(v.color)
        ? v.color
        : undefined,
    prompt: readPrompt(v.prompt),
    style:
      v.style === "pill" || v.style === "icon"
        ? (v.style as "pill" | "icon")
        : undefined,
  });
}

const share = (v: unknown): number | undefined =>
  typeof v === "number" && v >= 0 && v <= 1 ? v : undefined;

function readPanelBlock(v: unknown): ShellPanelBlock | null {
  if (!isObj(v)) return null;
  if (v.type === "rows")
    return {
      type: "rows",
      rows: list(v.rows, (r) => {
        if (!isObj(r)) return null;
        const label = str(r.label);
        const value = str(r.value);
        return label === undefined || value === undefined
          ? null
          : { label, value };
      }),
    };
  if (v.type === "text") {
    const text = str(v.text);
    return text === undefined ? null : { type: "text", text };
  }
  if (v.type === "heading") {
    const label = str(v.label);
    return label === undefined
      ? null
      : clean({ type: "heading" as const, label, detail: str(v.detail) });
  }
  if (v.type === "meter") {
    const label = str(v.label);
    const value = str(v.value);
    const filled = share(v.share);
    if (label === undefined || value === undefined || filled === undefined)
      return null;
    return clean({
      type: "meter" as const,
      label,
      detail: str(v.detail),
      value,
      share: filled,
      mark: share(v.mark),
      danger: v.danger === true ? true : undefined,
    });
  }
  return null;
}

function readPrompt(v: unknown): ShellAction["prompt"] {
  if (!isObj(v)) return undefined;
  const title = str(v.title);
  if (title === undefined) return undefined;
  return clean({
    title,
    placeholder: str(v.placeholder),
    max:
      typeof v.max === "number" && Number.isInteger(v.max) && v.max > 0
        ? v.max
        : undefined,
  });
}

function readPanel(v: unknown): ShellPanel | undefined {
  if (!isObj(v)) return undefined;
  const title = str(v.title);
  if (title === undefined) return undefined;
  const b = isObj(v.button) ? v.button : null;
  const id = b ? str(b.id) : undefined;
  const label = b ? str(b.label) : undefined;
  return clean({
    title,
    button:
      id && label !== undefined
        ? clean({
            id,
            label,
            disabled: b?.disabled === true ? true : undefined,
          })
        : undefined,
    failure: str(v.failure),
    blocks: list(v.blocks, readPanelBlock),
  });
}

function readActionItem(v: unknown): ShellAction | ShellMenuSeparator | null {
  return isObj(v) && v.type === "separator"
    ? { type: "separator" }
    : readAction(v);
}

function readChip(v: unknown): ShellChip | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  const label = str(v.label);
  if (!id || label === undefined) return null;
  return clean({
    id,
    label,
    icon: str(v.icon),
    items: Array.isArray(v.items) ? list(v.items, readActionItem) : undefined,
  });
}

export function readShellHeader(v: unknown): ShellHeader | null {
  if (!isObj(v)) return null;
  const title = str(v.title);
  if (title === undefined) return null;
  return clean({
    version: version(v.version),
    title,
    back: v.back === true ? true : undefined,
    over: v.over === true ? true : undefined,
    backAction: str(v.backAction),
    chips: Array.isArray(v.chips) ? list(v.chips, readChip) : undefined,
    note: str(v.note),
    actions: list(v.actions, readAction),
  });
}

/**
 * The `cover` message's cover, or null for none (and for one that cannot be
 * read: nothing is laid then).
 */
export function readShellCover(v: unknown): ShellCover | null {
  if (!isObj(v) || typeof v.shade !== "number" || !Number.isFinite(v.shade))
    return null;
  const shade = Math.min(1, Math.max(0, v.shade));
  return shade > 0 ? { shade, dismiss: v.dismiss === true } : null;
}

/** What the host says of itself, or null when it says nothing readable. */
export function readShellHostInfo(v: unknown): ShellHostInfo | null {
  if (!isObj(v)) return null;
  return {
    version: version(v.version),
    floats: strs(v.floats) ?? [],
    ...(v.phone === true ? { phone: true } : {}),
    ...(v.appSettings === true ? { appSettings: true } : {}),
    ...(v.viewer === true ? { viewer: true } : {}),
    ...(isObj(v.notices) &&
    str(v.notices.installationId) &&
    str(v.notices.device)
      ? {
          notices: {
            installationId: v.notices.installationId as string,
            device: v.notices.device as string,
          },
        }
      : {}),
  };
}

const FLOAT_KINDS: readonly string[] = [
  "to-end",
  "composer",
  "turn",
  "composer-turn",
] satisfies ShellFloatKind[];

function readAnchor(v: unknown): ShellFloatAnchor | undefined {
  if (!isObj(v)) return undefined;
  const { x, bottom } = v;
  return typeof x === "number" &&
    Number.isFinite(x) &&
    typeof bottom === "number" &&
    Number.isFinite(bottom)
    ? { x, bottom }
    : undefined;
}

function readFloat(v: unknown): ShellFloat | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  const label = str(v.label);
  if (!id || label === undefined || !FLOAT_KINDS.includes(String(v.kind)))
    return null;
  return clean({
    kind: v.kind as ShellFloatKind,
    id,
    label,
    anchor: readAnchor(v.anchor),
    tag: str(v.tag),
    note: str(v.note),
    fresh: v.fresh === true ? true : undefined,
  });
}

/** The floating parts of a `floats` message, skipping kinds this version does not know. */
export function readShellFloats(v: unknown): ShellFloat[] {
  return list(v, readFloat);
}

/** The buttons of a `tools` message, skipping those missing what they need. */
export function readShellTools(v: unknown): ShellAction[] {
  return list(v, readAction);
}

function readFavoriteRef(v: unknown): ShellFavoriteRef | null {
  if (!isObj(v) || (v.type !== "item" && v.type !== "folder")) return null;
  const id = str(v.id);
  return id ? { type: v.type, id } : null;
}

function readFavoriteChange(
  v: Record<string, unknown>,
): ShellMessageFromHost | null {
  const place = str(v.place);
  if (!place) return null;
  if (v.op === "move") {
    const entry = readFavoriteRef(v.entry);
    if (!entry) return null;
    return {
      type: "favorite",
      place,
      op: "move",
      entry,
      folder: str(v.folder) ?? null,
      before: readFavoriteRef(v.before),
    };
  }
  const name = str(v.name);
  const id = str(v.id);
  if (v.op === "folder-new" && name)
    return { type: "favorite", place, op: "folder-new", name };
  if (v.op === "folder-rename" && id && name)
    return { type: "favorite", place, op: "folder-rename", id, name };
  if (v.op === "folder-delete" && id)
    return { type: "favorite", place, op: "folder-delete", id };
  return null;
}

/** A message from the outside, or null for one this version does not know. */
export function readFromHost(v: unknown): ShellMessageFromHost | null {
  if (!isObj(v)) return null;
  if (v.type === "back") return { type: "back" };
  if (v.type === "over-drag")
    return typeof v.x === "number" && Number.isFinite(v.x)
      ? { type: "over-drag", x: Math.max(0, v.x) }
      : null;
  if (v.type === "over-drag-end")
    return typeof v.back === "boolean"
      ? { type: "over-drag-end", back: v.back }
      : null;
  if (v.type === "cover-pressed") return { type: "cover-pressed" };
  if (v.type === "notices") return readNoticesState(v);
  if (v.type === "open") {
    const path = str(v.path);
    return path?.startsWith("/") && !path.startsWith("//")
      ? { type: "open", path }
      : null;
  }
  if (v.type === "favorite") return readFavoriteChange(v);
  if (v.type === "keyboard")
    return typeof v.height === "number" &&
      Number.isFinite(v.height) &&
      v.height >= 0
      ? { type: "keyboard", height: v.height }
      : null;
  const id = str(v.id);
  if (!id) return null;
  if (v.type === "select") return { type: "select", id };
  if (v.type === "action")
    return typeof v.text === "string"
      ? { type: "action", id, text: v.text }
      : { type: "action", id };
  if (v.type === "composer") return readComposerEvent(v, id);
  if (v.type === "viewer-moved" || v.type === "viewer-closed")
    return readViewerEvent(v, v.type, id);
  return null;
}

function readViewerEvent(
  v: Raw,
  type: "viewer-moved" | "viewer-closed",
  id: string,
): ShellMessageFromHost | null {
  const index = v.index;
  if (typeof index !== "number" || !Number.isInteger(index) || index < 0)
    return null;
  if (type === "viewer-moved") return { type, id, index };
  const time =
    typeof v.time === "number" && Number.isFinite(v.time) && v.time >= 0
      ? v.time
      : undefined;
  return { type, id, index, ...(time !== undefined ? { time } : {}) };
}

const NOTICE_STATUSES: readonly string[] = [
  "authorized",
  "denied",
  "notDetermined",
] satisfies ShellNoticesState["status"][];

function readNoticesState(v: Raw): ShellMessageFromHost | null {
  const status = str(v.status);
  if (!status || !NOTICE_STATUSES.includes(status)) return null;
  const token = str(v.token);
  return {
    type: "notices",
    status: status as ShellNoticesState["status"],
    token: token && /^[0-9a-f]+$/i.test(token) ? token : null,
    environment: v.environment === "sandbox" ? "sandbox" : "production",
  };
}

function readUpload(v: unknown): ShellComposerUpload | null {
  if (!isObj(v)) return null;
  const name = str(v.name);
  const data = str(v.data);
  if (name === undefined || data === undefined) return null;
  return { name, mime: str(v.mime) ?? "", data };
}

function readComposerEvent(v: Raw, id: string): ShellMessageFromHost | null {
  switch (v.event) {
    case "text": {
      const text = str(v.text);
      if (text === undefined) return null;
      const caret =
        typeof v.caret === "number" && Number.isFinite(v.caret)
          ? v.caret
          : undefined;
      return {
        type: "composer",
        id,
        event: "text",
        text,
        ...(caret !== undefined ? { caret } : {}),
      };
    }
    case "pick": {
      const key = str(v.key);
      return key ? { type: "composer", id, event: "pick", key } : null;
    }
    case "send":
      return { type: "composer", id, event: "send" };
    case "attach":
      return {
        type: "composer",
        id,
        event: "attach",
        files: list(v.files, readUpload),
      };
    case "remove": {
      const file = str(v.file);
      return file ? { type: "composer", id, event: "remove", file } : null;
    }
    case "pill": {
      const pill = str(v.pill);
      return pill ? { type: "composer", id, event: "pill", pill } : null;
    }
    case "room":
      return typeof v.height === "number" && Number.isFinite(v.height)
        ? {
            type: "composer",
            id,
            event: "room",
            height: v.height,
            ...(v.keyboard === true ? { keyboard: true } : {}),
          }
        : null;
    case "turn": {
      const item = str(v.item);
      const turn = item ? readTurnEvent(v) : null;
      return item && turn
        ? { type: "composer", id, event: "turn", item, ...turn }
        : null;
    }
    default:
      return null;
  }
}

/** A `turn` event's action, or null for one unknown or missing what it needs. */
function readTurnEvent(v: Raw): ShellComposerTurnEvent | null {
  switch (v.action) {
    case "open":
      return { action: "open" };
    case "fold":
      return { action: "fold" };
    case "step":
      return v.by === 1 || v.by === -1 ? { action: "step", by: v.by } : null;
    case "option": {
      const option = str(v.option);
      return option ? { action: "option", option } : null;
    }
    case "field": {
      const field = str(v.field);
      const value = str(v.value);
      return field && value !== undefined
        ? { action: "field", field, value }
        : null;
    }
    case "draft": {
      const text = str(v.text);
      if (text === undefined) return null;
      const subject = str(v.subject);
      return {
        action: "draft",
        text,
        ...(subject !== undefined ? { subject } : {}),
      };
    }
    case "button": {
      const button = str(v.button);
      return button ? { action: "button", button } : null;
    }
    case "link": {
      const link = str(v.link);
      return link ? { action: "link", link } : null;
    }
    case "file": {
      const file = str(v.file);
      return file ? { action: "file", file } : null;
    }
    case "ref": {
      const kind = str(v.kind);
      const ref = str(v.ref);
      return kind && ref ? { action: "ref", kind, ref } : null;
    }
    default:
      return null;
  }
}
